import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Plus, Upload, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';

interface Props {
  /** Vagues plutôt que heats : le vocabulaire d'un événement Course. */
  isCourseEvent: boolean;
  /** Sans WOD ni catégorie, la génération n'a rien sur quoi travailler. */
  canGenerate: boolean;
  onGenerate: () => void;
  onCreate: () => void;
  onImport: () => void;
}

/**
 * Planning vide : une invitation à le construire, pas un constat de vide.
 *
 * C'était une simple alerte grise (« Aucun heat »), pendant que les seules
 * actions utiles se trouvaient en haut à droite, loin du regard. Un écran vide
 * est le moment où l'organisateur a le PLUS besoin qu'on lui dise quoi faire :
 * les actions viennent donc au centre, et la barre d'outils les retire pour ne
 * pas proposer deux fois la même chose.
 *
 * La génération est mise en avant parce que c'est le chemin normal — on crée un
 * heat à la main pour un rattrapage, pas pour bâtir une journée entière. Elle
 * est désactivée tant qu'il n'y a ni WOD ni catégorie : la proposer sur un
 * événement qui n'a rien à planifier mènerait droit à une erreur.
 */
const HeatsEmptyState: React.FC<Props> = ({
  isCourseEvent, canGenerate, onGenerate, onCreate, onImport,
}) => {
  const { t } = useTranslation();
  const k = isCourseEvent ? 'waves' : 'heats';

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-14 px-6 text-center">
      <CalendarClock size={28} className="mb-4 text-muted-foreground/60" />

      <h2 className="text-lg font-semibold">{t(`heats.empty.${k}.title`)}</h2>
      <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
        {t(canGenerate ? `heats.empty.${k}.body` : `heats.empty.needsSetup_${k}`)}
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        <Button onClick={onGenerate} disabled={!canGenerate}>
          <CalendarPlus size={18} />
          <span className="ml-2">{t('heats.actions.generate')}</span>
        </Button>
        <Button variant="secondary" onClick={onCreate}>
          <Plus size={18} />
          <span className="ml-2">{t('heats.actions.create')}</span>
        </Button>
      </div>

      {/* L'import existe surtout pour reprendre un planning fait ailleurs :
          troisième chemin, donc troisième niveau visuel. */}
      <Button
        variant="ghost" size="sm"
        onClick={onImport}
        className="mt-3 text-muted-foreground hover:text-foreground"
      >
        <Upload size={15} />
        <span className="ml-2">{t('heats.empty.import')}</span>
      </Button>
    </div>
  );
};

export default HeatsEmptyState;
